import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { useAppStore } from '@src/app/context'

import * as styles from './VolumeIndicator.css'
import useAudioDevice from './useAudioDevice'

type Intensity = 'muted' | 'green' | 'yellow' | 'red'

const NUM_OF_VOLUME_INDICATORS = new Array(16).fill(null)

type VolumeIndicatorProps = {
  isDisabled?: boolean
}

const VolumeIndicator = ({ isDisabled = false }: VolumeIndicatorProps) => {
  const [volumeLevel, setVolumeLevel] = useState(0)
  const { service } = useAppStore()
  const devices = [...service.voice.inputDevices.values()]
  const audioDevice = useAudioDevice('microphone', devices)
  const selectedDevice = audioDevice.activeDevice.deviceId

  const getIntensity = (percentage: number, soundPercentage: number): Intensity => {
    if (percentage > soundPercentage || soundPercentage === 0) {
      return 'muted'
    }
    if (percentage < 50) {
      return 'green'
    }
    if (percentage < 90) {
      return 'yellow'
    }
    return 'red'
  }

  useEffect(() => {
    if (isDisabled) {
      setVolumeLevel(0)
      return
    }

    let animationFrameId: number
    let stream: MediaStream
    let audioContext: AudioContext
    let analyser: AnalyserNode
    let source: MediaStreamAudioSourceNode

    const getAudioStream = async () => {
      if (selectedDevice) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: selectedDevice ? { exact: selectedDevice } : undefined,
          },
        })
        audioContext = new window.AudioContext()
        source = stream && audioContext.createMediaStreamSource(stream)
        analyser = audioContext.createAnalyser()
        analyser.fftSize = 2048
        analyser.smoothingTimeConstant = 0.3
        source?.connect(analyser)
        const bufferLength = analyser.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)
        const draw = () => {
          analyser.getByteFrequencyData(dataArray)
          // Calculate an overall volume value
          let values = 0
          for (let i = 0; i < bufferLength; i++) {
            // @ts-expect-error unchecked index access
            values += dataArray[i]
          }
          const average = values / bufferLength

          // Update the volume state with the new volume value
          setVolumeLevel(average)

          animationFrameId = requestAnimationFrame(draw)
        }

        draw()
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    getAudioStream()

    return () => {
      cancelAnimationFrame(animationFrameId)

      stream?.getAudioTracks().forEach((track) => track.stop())
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      audioContext?.close()
      source?.disconnect()
      analyser?.disconnect()
    }
  }, [isDisabled, selectedDevice])

  return (
    <div className={styles.volumeIndicatorsContainer}>
      {NUM_OF_VOLUME_INDICATORS.map((_, index) => {
        const percentage = (index * 100) / (NUM_OF_VOLUME_INDICATORS.length - 1)
        const volumeIntensity = getIntensity(percentage, volumeLevel)
        return (
          <div
            className={styles.volumeIndicator({
              volumeIntensity,
            })}
            key={index}
          />
        )
      })}
    </div>
  )
}

export default observer(VolumeIndicator)
